import {
  deserify,
  IStyles,
  pxToRem,
  pxTovW,
  StepsBadge,
} from '@geneo2-web/shared-ui';
import SensorsIcon from '@mui/icons-material/Sensors';
import QuizIcon from '@mui/icons-material/Quiz';
import GroupsIcon from '@mui/icons-material/Groups';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../reduxStore/reduxHooks';
import { LIVE_QUIZ_ANSWER } from '../../routeHandling/RoutesNomenclature';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(20), md: pxTovW(20) },
    paddingBottom: { xs: pxToRem(60), md: pxTovW(40) },
    paddingTop: { xs: pxToRem(30), md: pxTovW(30) },
    maxWidth: { xs: '100%', md: pxTovW(600) },
    mx: 'auto',
    textAlign: 'center',
  },
  detailsCard: {
    border: '1px solid #EDE7FF',
    backgroundColor: '#FAF8FF',
    borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    p: { xs: pxToRem(18), md: pxTovW(22) },
    mt: { xs: pxToRem(25), md: pxTovW(30) },
    textAlign: 'left',
  },
  stepBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: { xs: pxToRem(14), md: pxTovW(15) },
    flexDirection: { xs: 'column', sm: 'row' },
  },
};

// Same "Welcome / Follow these steps" heading + numbered StepsBadge pattern
// used on the teacher-side LiveQuizLobby's right panel (which itself
// replicates ClickerAssessmentStart.tsx's design) — kept in the LiveQuiz
// violet identity already established by LiveQuizJoinCard rather than
// switching to Clicker's yellow/blue/red, since that's this feature's own
// brand on the student side. The student never navigates themselves — this
// screen just waits for the LiveQuizContextProvider's Firestore-triggered
// resync to populate current_question, then moves on automatically.
export const LiveQuizWaitingRoom = () => {
  const navigate = useNavigate();
  const { current_question, joined_count, live_quiz_session, questions } =
    deserify(useAppSelector((state) => state.liveQuiz));

  useEffect(() => {
    if (current_question) {
      navigate(LIVE_QUIZ_ANSWER);
    }
  }, [current_question, navigate]);

  const questionCount = live_quiz_session?.totalQuestions || questions.length;

  return (
    <Box sx={styles.root}>
      <Box
        sx={{
          width: { xs: pxToRem(64), md: pxTovW(70) },
          height: { xs: pxToRem(64), md: pxTovW(70) },
          borderRadius: '50%',
          backgroundColor: '#7C4DFF',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: { xs: pxToRem(16), md: pxTovW(18) },
        }}
      >
        <SensorsIcon sx={{ fontSize: { xs: pxToRem(30), md: pxTovW(32) } }} />
      </Box>

      <Typography
        variant="h1"
        sx={{ fontSize: { xs: pxToRem(18), md: pxTovW(30) } }}
      >
        Welcome To Your Live Quiz.
      </Typography>
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: pxToRem(13), md: pxTovW(20) },
          color: '#666666',
          mt: pxToRem(6),
        }}
      >
        Waiting for your teacher to start — here's what happens next.
      </Typography>

      <CircularProgress
        size={26}
        sx={{ my: { xs: pxToRem(18), md: pxTovW(20) }, color: '#7C4DFF' }}
      />

      {joined_count !== undefined && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <GroupsIcon fontSize="small" sx={{ color: '#7C4DFF' }} />
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: pxToRem(13), md: pxTovW(20) },
              color: '#666666',
            }}
          >
            {joined_count} student{joined_count === 1 ? '' : 's'} joined so far
          </Typography>
        </Box>
      )}

      {live_quiz_session?.taskTitle && (
        <Box sx={styles.detailsCard}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ fontSize: { xs: pxToRem(16), md: pxTovW(22) } }}
          >
            {live_quiz_session.taskTitle}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: pxToRem(13), md: pxTovW(18) },
              color: '#666666',
              mt: pxToRem(4),
              mb: pxToRem(10),
            }}
          >
            {[
              live_quiz_session.subject,
              [live_quiz_session.className, live_quiz_session.sectionName]
                .filter(Boolean)
                .join(' '),
            ]
              .filter(Boolean)
              .join(' · ')}
            {live_quiz_session.teacherName
              ? ` · with ${live_quiz_session.teacherName}`
              : ''}
          </Typography>
          {!!questionCount && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuizIcon fontSize="small" sx={{ color: '#7C4DFF' }} />
              <Typography
                variant="h4"
                sx={{ fontSize: { xs: pxToRem(13), md: pxTovW(18) } }}
              >
                {questionCount} question{questionCount === 1 ? '' : 's'} in this
                quiz
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: { xs: pxToRem(30), md: pxTovW(35) },
          gap: { xs: pxToRem(20), md: pxTovW(24) },
        }}
      >
        {WAITING_ROOM_STEPS.map((info, index) => (
          <Box key={info.stepInstructions} sx={styles.stepBox}>
            <StepsBadge
              number={index + 1}
              backgroundColor={info.countBackground}
              bordercolor={info.countBorderColor}
              rootStyles={{
                width: { xs: pxToRem(90), md: pxTovW(110) },
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                fontSize: { xs: pxToRem(13), md: pxTovW(18) },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              {info.stepInstructions}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Three tints lifted from LiveQuizJoinCard's own violet→pink gradient
// (#7C4DFF / #B36BFF / #FF6FB0), so this screen visually continues the same
// "join" moment rather than introducing a fresh, unrelated palette. Text is
// the same content the previous plain icon+text tip list used.
const WAITING_ROOM_STEPS = [
  {
    countBackground: '#EDE7FF',
    countBorderColor: '#7C4DFF',
    stepInstructions:
      'Stay on this screen — your next question will appear here automatically.',
  },
  {
    countBackground: '#F3E5FF',
    countBorderColor: '#B36BFF',
    stepInstructions:
      'Pick your answer and submit it before your teacher moves on.',
  },
  {
    countBackground: '#FFE6F0',
    countBorderColor: '#FF6FB0',
    stepInstructions:
      "Your teacher reveals whether you were right after everyone's answered.",
  },
];

export default LiveQuizWaitingRoom;
